/**
 * Avatar configuration types.
 *
 * Shapes follow the DiceBear 10 option names. Anything persisted before the v10
 * upgrade used the v9 names (`radius`, `eyes`, a boolean `flip`, percent
 * `scale`), which v10 rejects outright — hence the version stamp below.
 */

export const AVATAR_CONFIG_VERSION = 10;

export type AvatarFlip = 'none' | 'horizontal' | 'vertical' | 'both';

export interface AvatarOptions {
  style: string;
  seed: string;
  size: number;
  /** Hex colors without `#`; `00000000` for transparent. Empty = no background. */
  backgroundColor: string[];
  /** 0–50 (percent of the canvas). Named `radius` before v10. */
  borderRadius: number;
  /** Multiplier, 0–10, where 1 is unscaled. Was a 0–200 percentage before v10. */
  scale: number;
  rotate: number;
  translateX: number;
  translateY: number;
  /** Was a boolean before v10. */
  flip: AvatarFlip;
}

/** Canvas defaults recorded alongside a saved avatar. */
export const defaultAvatarOptions: AvatarOptions = {
  style: 'avataaars',
  seed: 'Felix',
  size: 256,
  backgroundColor: ['00000000'],
  borderRadius: 0,
  scale: 1,
  rotate: 0,
  translateX: 0,
  translateY: 0,
  flip: 'none',
};

export interface StyleSpecificOptions {
  [key: string]: unknown;
}

/**
 * Complete avatar configuration that can be saved and restored
 */
export interface AvatarConfig {
  /** Absent on configs written before the DiceBear 10 upgrade. */
  version?: number;
  style: string; // e.g., 'adventurer', 'avataaars'
  seed: string; // Random seed for uniqueness
  options: AvatarOptions;
  styleOptions: StyleSpecificOptions;
  svg?: string; // SVG markup (optional, can be regenerated)
  dataUri?: string; // Data URI for quick display (optional)
}

/**
 * A stored config is only replayable in the editor when it was written against
 * the current DiceBear major. Older ones still render from their saved
 * `avatar_svg`, but their option names would fail v10 validation.
 */
export const isReplayableAvatarConfig = (
  config: AvatarConfig | null | undefined,
): config is AvatarConfig => config?.version === AVATAR_CONFIG_VERSION;

/**
 * Avatar entity from Strapi (one-to-one with User)
 */
export interface Avatar {
  id: number;
  documentId?: string;
  style: string;
  seed: string;
  avatar_config: AvatarConfig;
  avatar_svg: string | null;
  user_id: number;
  createdAt?: string;
  updatedAt?: string;
}
